/**
 * The commercial starter library: the rooms and tasks a janitorial spec is
 * written in, seeded the FIRST time a company switches commercial work on
 * (`companies.commercial_templates_seeded_at`) rather than at registration —
 * a residential company's task list should not open with "disinfect restroom
 * fixtures" on it.
 *
 * Same contract as `seed-templates.ts`: these are only defaults, ordinary
 * editable rows the moment they land, and the English arrays are the checked
 * shape every translation must cover in full. Timings are mid-range figures
 * for ONE cleaner — commercial crews commonly work solo — where the
 * residential library assumes a crew of two.
 *
 * Keys are prefixed `c_` so the two libraries can never collide in the maps
 * that wire rooms to tasks, and so a future merge of the files has nothing to
 * rename.
 */

import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type SupportedLanguage } from './languages.ts';
import type { LocalizedText } from './localized-text.ts';
import type {
  LocalizedSeedRoomTemplate,
  LocalizedSeedServiceTemplate,
  SeedRoomService,
  SeedRoomTemplate,
  SeedServiceTemplate,
} from './seed-templates.ts';
import { seedField } from './seed-templates.ts';
import { COMMERCIAL_SEED_TRANSLATIONS_ES } from './seed-templates-commercial.es.ts';
import { REFERENCE_PRODUCTION_RATES, referenceRateFor } from './production-rates.ts';
import { commercialTags, guessRoomCategory, guessTaskCategory, type RoomCategory } from './template-taxonomy.ts';

const COMMERCIAL_SERVICE_SOURCE = [
  // --- Janitorial (the nightly routine) ------------------------------------
  {
    key: 'c_empty_trash',
    name: 'Empty trash and replace liners',
    customerDescription: 'Empty all waste bins, replace liners, and carry trash to the dumpster.',
    category: 'Janitorial',
    defaultMinutes: 3,
    minimumMinutes: 1,
    maximumMinutes: 15,
  },
  {
    key: 'c_dust_surfaces',
    name: 'Dust horizontal surfaces',
    customerDescription: 'Dust desks, ledges, sills and other reachable horizontal surfaces.',
    category: 'Janitorial',
    defaultMinutes: 6,
    minimumMinutes: 2,
    maximumMinutes: 30,
  },
  {
    key: 'c_vacuum_carpet',
    name: 'Vacuum carpeted areas',
    customerDescription: 'Vacuum carpet and walk-off mats, including edges and under desks where clear.',
    category: 'Janitorial',
    defaultMinutes: 10,
    minimumMinutes: 4,
    maximumMinutes: 45,
  },
  {
    key: 'c_mop_hard_floors',
    name: 'Dust mop and damp mop hard floors',
    customerDescription: 'Dust mop, then damp mop hard flooring with a neutral cleaner.',
    category: 'Janitorial',
    defaultMinutes: 10,
    minimumMinutes: 4,
    maximumMinutes: 45,
  },
  {
    key: 'c_glass_doors',
    name: 'Clean entry glass and doors',
    customerDescription: 'Spot clean entrance glass, door frames and push plates.',
    category: 'Janitorial',
    defaultMinutes: 5,
    minimumMinutes: 2,
    maximumMinutes: 20,
  },
  {
    key: 'c_restroom_fixtures',
    name: 'Clean and disinfect restroom fixtures',
    customerDescription: 'Clean and disinfect toilets, urinals, sinks, counters and touch points.',
    category: 'Janitorial',
    defaultMinutes: 15,
    minimumMinutes: 5,
    maximumMinutes: 60,
  },
  {
    key: 'c_restock_consumables',
    name: 'Restock paper and soap',
    customerDescription: 'Refill towel, tissue and soap dispensers from on-site stock.',
    category: 'Janitorial',
    defaultMinutes: 4,
    minimumMinutes: 1,
    maximumMinutes: 20,
  },
  {
    key: 'c_break_room_surfaces',
    name: 'Clean break room counters and sink',
    customerDescription: 'Wipe counters, sink, table tops and the outside of appliances; clean the microwave inside.',
    category: 'Janitorial',
    defaultMinutes: 8,
    minimumMinutes: 3,
    maximumMinutes: 30,
  },
  {
    key: 'c_spot_clean_touch_points',
    name: 'Spot clean walls and touch points',
    customerDescription: 'Spot clean marks on walls, light switches, handles and rails.',
    category: 'Janitorial',
    defaultMinutes: 6,
    minimumMinutes: 2,
    maximumMinutes: 30,
  },
  // --- Periodic (the tasks a spec schedules by the month or quarter) -------
  {
    key: 'c_high_dust',
    name: 'High dust vents and ledges',
    customerDescription: 'Dust air vents, high ledges, partition tops and door frames.',
    category: 'Periodic',
    defaultMinutes: 8,
    minimumMinutes: 3,
    maximumMinutes: 40,
  },
  {
    key: 'c_buff_floors',
    name: 'Machine buff hard floors',
    customerDescription: 'Machine buff or burnish hard floors to restore shine.',
    category: 'Periodic',
    defaultMinutes: 30,
    minimumMinutes: 10,
    maximumMinutes: 120,
  },
  {
    key: 'c_carpet_extraction',
    name: 'Carpet extraction',
    customerDescription: 'Deep clean carpet with hot water extraction.',
    category: 'Periodic',
    defaultMinutes: 45,
    minimumMinutes: 15,
    maximumMinutes: 240,
  },
  {
    key: 'c_strip_and_wax',
    name: 'Strip and wax hard floors',
    customerDescription: 'Strip old finish, neutralise, and lay fresh coats of floor finish.',
    category: 'Periodic',
    defaultMinutes: 120,
    minimumMinutes: 30,
    maximumMinutes: 600,
  },
  {
    key: 'c_auto_scrub',
    name: 'Auto-scrub hard floors',
    customerDescription: 'Machine scrub hard floors with an automatic scrubber.',
    category: 'Periodic',
    defaultMinutes: 30,
    minimumMinutes: 10,
    maximumMinutes: 240,
  },
  // --- Restroom fixtures ---------------------------------------------------
  //
  // Split out from `c_restroom_fixtures` for the estimator who counts them
  // separately, which is most of them: a restroom's work is its fixtures, and
  // toilets, urinals and sinks are not the same minutes each. Both live in
  // the library — the combined line for a quick bid, these three for a
  // walkthrough that counted.
  {
    key: 'c_clean_toilets',
    name: 'Clean and disinfect toilets',
    customerDescription: 'Clean and disinfect toilet bowls, seats, bases and flush handles.',
    category: 'Janitorial',
    defaultMinutes: 3,
    minimumMinutes: 1,
    maximumMinutes: 15,
  },
  {
    key: 'c_clean_urinals',
    name: 'Clean and disinfect urinals',
    customerDescription: 'Clean and disinfect urinals, screens and flush valves.',
    category: 'Janitorial',
    defaultMinutes: 2,
    minimumMinutes: 1,
    maximumMinutes: 10,
  },
  {
    key: 'c_clean_sinks',
    name: 'Clean sinks and counters',
    customerDescription: 'Clean sinks, taps, counters and the mirrors above them.',
    category: 'Janitorial',
    defaultMinutes: 2,
    minimumMinutes: 1,
    maximumMinutes: 10,
  },
  // --- Warehouse and industrial -------------------------------------------
  {
    key: 'c_sweep_warehouse',
    name: 'Sweep warehouse floor',
    customerDescription: 'Sweep or dust mop open warehouse floor and aisles.',
    category: 'Janitorial',
    defaultMinutes: 30,
    minimumMinutes: 10,
    maximumMinutes: 240,
  },
  {
    key: 'c_clean_elevator',
    name: 'Clean elevator cab',
    customerDescription: 'Wipe walls, rails, buttons and the floor of one elevator cab. An editable estimate.',
    category: 'Janitorial',
    defaultMinutes: 8,
    minimumMinutes: 3,
    maximumMinutes: 20,
  },
  {
    key: 'c_clean_lockers',
    name: 'Clean lockers',
    customerDescription: 'Wipe locker fronts and benches. Count the lockers.',
    category: 'Janitorial',
    defaultMinutes: 3,
    minimumMinutes: 1,
    maximumMinutes: 10,
  },
  {
    key: 'c_clean_exam_table',
    name: 'Clean exam table',
    customerDescription: 'Disinfect the exam table, paper roll and nearby counters. An editable estimate.',
    category: 'Janitorial',
    defaultMinutes: 8,
    minimumMinutes: 3,
    maximumMinutes: 20,
  },
  {
    key: 'c_sanitize_surfaces',
    name: 'Sanitize touch surfaces',
    customerDescription: 'Disinfect high-touch surfaces. An editable estimate, measured by area when the room is.',
    category: 'Janitorial',
    defaultMinutes: 10,
    minimumMinutes: 3,
    maximumMinutes: 60,
  },
  {
    key: 'c_dust_merchandise',
    name: 'Dust merchandise and displays',
    customerDescription: 'Dust shelves and displays without moving product. An editable estimate.',
    category: 'Janitorial',
    defaultMinutes: 15,
    minimumMinutes: 5,
    maximumMinutes: 90,
  },
  {
    key: 'c_clean_machines',
    name: 'Clean laundry machines',
    customerDescription: 'Wipe the exterior of one washer or dryer. Count the machines.',
    category: 'Janitorial',
    defaultMinutes: 5,
    minimumMinutes: 2,
    maximumMinutes: 15,
  },
] as const satisfies readonly SeedServiceTemplate[];

const service = (serviceKey: CommercialSeedServiceKey, defaultSelected: boolean): SeedRoomService => ({
  serviceKey,
  defaultSelected,
});

const COMMERCIAL_ROOM_SOURCE = [
  {
    key: 'c_open_office',
    name: 'Open office',
    description: 'Desks, cubicles and open work areas. Measured in square feet.',
    iconName: 'desk',
    measurementBasis: 'area',
    defaultFloorType: 'carpet',
    services: [
      service('c_empty_trash', true),
      service('c_dust_surfaces', true),
      service('c_vacuum_carpet', true),
      service('c_spot_clean_touch_points', false),
      service('c_high_dust', false),
      service('c_carpet_extraction', false),
    ],
  },
  {
    key: 'c_private_office',
    name: 'Private office',
    description: 'An enclosed office or a small suite of them. Measured in square feet.',
    iconName: 'desk',
    measurementBasis: 'area',
    defaultFloorType: 'carpet',
    services: [
      service('c_empty_trash', true),
      service('c_dust_surfaces', true),
      service('c_vacuum_carpet', true),
      service('c_high_dust', false),
    ],
  },
  {
    key: 'c_restroom',
    name: 'Restroom',
    description: 'Priced per fixture — count the toilets, urinals and sinks.',
    iconName: 'toilet',
    measurementBasis: 'fixture',
    defaultFloorType: 'hard',
    services: [
      service('c_clean_toilets', true),
      service('c_clean_urinals', true),
      service('c_clean_sinks', true),
      service('c_restock_consumables', true),
      service('c_mop_hard_floors', true),
      service('c_empty_trash', true),
      service('c_restroom_fixtures', false),
      service('c_high_dust', false),
    ],
  },
  {
    key: 'c_break_room',
    name: 'Break room',
    description: 'Kitchenette, lunch room or coffee point.',
    iconName: 'utensils',
    measurementBasis: 'area',
    defaultFloorType: 'resilient',
    services: [
      service('c_break_room_surfaces', true),
      service('c_empty_trash', true),
      service('c_mop_hard_floors', true),
      service('c_restock_consumables', false),
    ],
  },
  {
    key: 'c_lobby',
    name: 'Lobby and entrance',
    description: 'Reception, entrance and the first impression.',
    iconName: 'door',
    measurementBasis: 'area',
    defaultFloorType: 'hard',
    services: [
      service('c_glass_doors', true),
      service('c_dust_surfaces', true),
      service('c_vacuum_carpet', true),
      service('c_mop_hard_floors', false),
      service('c_empty_trash', true),
      service('c_buff_floors', false),
    ],
  },
  {
    key: 'c_conference_room',
    name: 'Conference room',
    description: 'Meeting and training rooms.',
    iconName: 'sofa',
    measurementBasis: 'area',
    defaultFloorType: 'carpet',
    services: [
      service('c_dust_surfaces', true),
      service('c_vacuum_carpet', true),
      service('c_empty_trash', true),
      service('c_spot_clean_touch_points', false),
    ],
  },
  {
    key: 'c_corridor',
    name: 'Hallway and stairs',
    description: 'Corridors, stairwells and shared circulation.',
    iconName: 'stairs',
    measurementBasis: 'area',
    defaultFloorType: 'mixed',
    services: [
      service('c_vacuum_carpet', true),
      service('c_mop_hard_floors', false),
      service('c_spot_clean_touch_points', false),
      service('c_high_dust', false),
    ],
  },
  {
    key: 'c_warehouse',
    name: 'Warehouse and industrial',
    description: 'Racking, aisles and dock areas — measured in square feet.',
    iconName: 'box',
    measurementBasis: 'area',
    defaultFloorType: 'concrete',
    services: [
      service('c_sweep_warehouse', true),
      service('c_empty_trash', true),
      service('c_auto_scrub', false),
      service('c_spot_clean_touch_points', false),
      service('c_high_dust', false),
    ],
  },
  {
    key: 'c_cubicles',
    name: 'Cubicles',
    description: 'A run of cubicles. Set the quantity to how many, or measure the bay.',
    iconName: 'desk',
    measurementBasis: 'area',
    defaultFloorType: 'carpet',
    categoryCode: 'offices',
    defaultQuantity: 1,
    services: [
      service('c_empty_trash', true),
      service('c_dust_surfaces', true),
      service('c_vacuum_carpet', true),
      service('c_sanitize_surfaces', false),
    ],
  },
  {
    key: 'c_hallway_carpet',
    name: 'Hallway, carpet',
    description: 'Carpeted corridor. Measure the square feet. Times follow the production rate.',
    iconName: 'stairs',
    measurementBasis: 'area',
    defaultFloorType: 'carpet',
    categoryCode: 'entries',
    services: [
      service('c_vacuum_carpet', true),
      service('c_spot_clean_touch_points', false),
      service('c_high_dust', false),
    ],
  },
  {
    key: 'c_hallway_hard',
    name: 'Hallway, hard floor',
    description: 'Hard-floor corridor. Measure the square feet.',
    iconName: 'stairs',
    measurementBasis: 'area',
    defaultFloorType: 'hard',
    categoryCode: 'entries',
    services: [
      service('c_mop_hard_floors', true),
      service('c_spot_clean_touch_points', false),
      service('c_dust_surfaces', false),
    ],
  },
  {
    key: 'c_stairwell',
    name: 'Stairwell',
    description: 'One flight and its landing. Set the quantity to the number of flights.',
    iconName: 'stairs',
    categoryCode: 'entries',
    defaultQuantity: 1,
    services: [
      service('c_vacuum_carpet', true),
      service('c_mop_hard_floors', false),
      service('c_spot_clean_touch_points', true),
      service('c_high_dust', false),
    ],
  },
  {
    key: 'c_elevator',
    name: 'Elevator',
    description: 'One cab. Set the quantity to the number of elevators.',
    iconName: 'door',
    categoryCode: 'entries',
    services: [service('c_clean_elevator', true), service('c_spot_clean_touch_points', true)],
  },
  {
    key: 'c_laundry_room',
    name: 'Laundry room',
    description: 'Common laundry. Count the machines.',
    iconName: 'washer',
    measurementBasis: 'fixture',
    defaultFloorType: 'resilient',
    categoryCode: 'laundry',
    services: [
      service('c_clean_machines', true),
      service('c_mop_hard_floors', true),
      service('c_empty_trash', true),
      service('c_dust_surfaces', false),
    ],
  },
  {
    key: 'c_mailroom',
    name: 'Mailroom',
    description: 'Mail slots, packages and the counter.',
    iconName: 'box',
    measurementBasis: 'area',
    defaultFloorType: 'resilient',
    categoryCode: 'common',
    services: [service('c_dust_surfaces', true), service('c_empty_trash', true), service('c_mop_hard_floors', true)],
  },
  {
    key: 'c_trash_room',
    name: 'Trash room',
    description: 'Trash or recycling room. An editable estimate.',
    iconName: 'trash',
    categoryCode: 'specialty',
    services: [
      service('c_empty_trash', true),
      service('c_mop_hard_floors', true),
      service('c_sanitize_surfaces', true),
    ],
  },
  {
    key: 'c_gym',
    name: 'Gym',
    description: 'Fitness room. Measured in square feet. Times are editable estimates.',
    iconName: 'activity',
    measurementBasis: 'area',
    defaultFloorType: 'resilient',
    categoryCode: 'specialty',
    services: [
      service('c_sanitize_surfaces', true),
      service('c_mop_hard_floors', true),
      service('c_empty_trash', true),
      service('c_glass_doors', false),
    ],
  },
  {
    key: 'c_locker_room',
    name: 'Locker room',
    description: 'Lockers, benches and the floor. Count the lockers.',
    iconName: 'closet',
    measurementBasis: 'fixture',
    defaultFloorType: 'hard',
    categoryCode: 'specialty',
    services: [
      service('c_clean_lockers', true),
      service('c_mop_hard_floors', true),
      service('c_sanitize_surfaces', true),
      service('c_empty_trash', true),
    ],
  },
  {
    key: 'c_exam_room',
    name: 'Medical exam room',
    description: 'One exam room. Times are editable estimates, not a clinical protocol.',
    iconName: 'medical',
    categoryCode: 'specialty',
    services: [
      service('c_clean_exam_table', true),
      service('c_sanitize_surfaces', true),
      service('c_mop_hard_floors', true),
      service('c_empty_trash', true),
    ],
  },
  {
    key: 'c_classroom',
    name: 'Classroom',
    description: 'Classroom or training room. Measured in square feet.',
    iconName: 'desk',
    measurementBasis: 'area',
    defaultFloorType: 'resilient',
    categoryCode: 'offices',
    services: [
      service('c_empty_trash', true),
      service('c_dust_surfaces', true),
      service('c_mop_hard_floors', true),
      service('c_vacuum_carpet', false),
      service('c_sanitize_surfaces', false),
    ],
  },
  {
    key: 'c_retail_floor',
    name: 'Retail floor',
    description: 'Sales floor. Measured in square feet. Dusting displays is an editable estimate.',
    iconName: 'store',
    measurementBasis: 'area',
    defaultFloorType: 'hard',
    categoryCode: 'specialty',
    services: [
      service('c_dust_merchandise', true),
      service('c_mop_hard_floors', true),
      service('c_empty_trash', true),
      service('c_vacuum_carpet', false),
      service('c_glass_doors', false),
    ],
  },
] as const satisfies readonly SeedRoomTemplate[];

/**
 * The measurement a task is seeded with, taken from the reference table
 * rather than repeated here.
 *
 * One place holds the numbers — `production-rates.ts`, next to the reasoning
 * for each of them — and this file holds the vocabulary. A task with no entry
 * in that table is seeded as a plain timed task, which is the honest answer:
 * nobody has a published rate for "clean break room counters", and inventing
 * one to fill the column would dress a guess up as a measurement.
 */
function withReferenceRate(template: SeedServiceTemplate): SeedServiceTemplate {
  const reference = REFERENCE_PRODUCTION_RATES[template.key];
  if (!reference) return template;
  return {
    ...template,
    measurementBasis: reference.basis,
    productionRatePerHour: reference.defaultRatePerHour,
  };
}

/**
 * The rate a task runs at in THIS kind of area, when the reference table says
 * it differs from the task's own default.
 *
 * Derived rather than typed onto each link, because the difference is not an
 * editorial choice — it is the floor. Mopping between restroom fixtures and
 * mopping open sealed concrete are the same task at half the rate, and the one
 * place that knows it is the table keyed by floor type.
 *
 * `undefined` when the two agree, so the seeded link says "use the task's
 * rate" rather than restating it — the same rule `minutesOverride` follows.
 */
function withFloorTypeRate(room: SeedRoomTemplate, link: SeedRoomService): SeedRoomService {
  const forFloor = referenceRateFor(link.serviceKey, room.defaultFloorType ?? null);
  const taskDefault = REFERENCE_PRODUCTION_RATES[link.serviceKey]?.defaultRatePerHour ?? null;
  if (forFloor === null || forFloor === taskDefault) return link;
  return { ...link, productionRateOverride: forFloor };
}

function withFloorTypeRates(room: SeedRoomTemplate): SeedRoomTemplate {
  return { ...room, services: room.services.map((link) => withFloorTypeRate(room, link)) };
}

export const COMMERCIAL_SEED_SERVICE_TEMPLATES: readonly SeedServiceTemplate[] =
  COMMERCIAL_SERVICE_SOURCE.map(withReferenceRate);
export const COMMERCIAL_SEED_ROOM_TEMPLATES: readonly SeedRoomTemplate[] =
  COMMERCIAL_ROOM_SOURCE.map(withFloorTypeRates);

// ---------------------------------------------------------------------------
// Translations — the same total-record contract as the residential library:
// a task added above is a compile error in every translation until covered.
// ---------------------------------------------------------------------------

export type CommercialSeedServiceKey = (typeof COMMERCIAL_SERVICE_SOURCE)[number]['key'];
export type CommercialSeedRoomKey = (typeof COMMERCIAL_ROOM_SOURCE)[number]['key'];
export type CommercialSeedCategory = (typeof COMMERCIAL_SERVICE_SOURCE)[number]['category'];

export interface CommercialSeedTranslations {
  readonly services: Readonly<
    Record<CommercialSeedServiceKey, { readonly name: string; readonly customerDescription: string }>
  >;
  readonly rooms: Readonly<Record<CommercialSeedRoomKey, { readonly name: string; readonly description: string }>>;
  readonly categories: Readonly<Record<CommercialSeedCategory, string>>;
}

const COMMERCIAL_SEED_TRANSLATIONS: Readonly<
  Record<Exclude<SupportedLanguage, typeof DEFAULT_LANGUAGE>, CommercialSeedTranslations>
> = {
  es: COMMERCIAL_SEED_TRANSLATIONS_ES,
};

function translationsFor(language: SupportedLanguage): CommercialSeedTranslations | null {
  return language === DEFAULT_LANGUAGE ? null : COMMERCIAL_SEED_TRANSLATIONS[language];
}

/** As the residential library's `acrossLanguages`, over the commercial one. */
function acrossLanguages(read: (translations: CommercialSeedTranslations | null) => string): LocalizedText {
  const collected: LocalizedText = {};
  for (const language of SUPPORTED_LANGUAGES) collected[language] = read(translationsFor(language));
  return collected;
}

/** The commercial task library in `language`, plus its wording in the others. */
export function commercialSeedServiceTemplates(language: SupportedLanguage): readonly LocalizedSeedServiceTemplate[] {
  const translations = translationsFor(language);

  return COMMERCIAL_SERVICE_SOURCE.map((template) => ({
    categoryCode: guessTaskCategory(template.name),
    tags: commercialTags(
      ['c_high_dust', 'c_carpet_extraction', 'c_strip_and_wax', 'c_auto_scrub', 'c_buff_floors'].includes(template.key)
        ? ['deep']
        : ['standard'],
    ),
    ...withReferenceRate({
      ...template,
      name: translations?.services[template.key].name ?? template.name,
      customerDescription: translations?.services[template.key].customerDescription ?? template.customerDescription,
      category: translations?.categories[template.category] ?? template.category,
    }),
    nameTranslations: acrossLanguages((other) => other?.services[template.key].name ?? template.name),
    customerDescriptionTranslations: acrossLanguages(
      (other) => other?.services[template.key].customerDescription ?? template.customerDescription,
    ),
    categoryTranslations: acrossLanguages((other) => other?.categories[template.category] ?? template.category),
  }));
}

/**
 * The commercial room library in `language`, with its task wiring unchanged
 * and its wording in the other languages alongside.
 */
export function commercialSeedRoomTemplates(language: SupportedLanguage): readonly LocalizedSeedRoomTemplate[] {
  const translations = translationsFor(language);

  return COMMERCIAL_ROOM_SOURCE.map((room) => ({
    categoryCode: seedField<RoomCategory>(room, 'categoryCode') ?? guessRoomCategory(room.name, room.description),
    tags: commercialTags(),
    defaultQuantity: seedField<number>(room, 'defaultQuantity') ?? 1,
    ...withFloorTypeRates({
      ...room,
      name: translations?.rooms[room.key].name ?? room.name,
      description: translations?.rooms[room.key].description ?? room.description,
    }),
    nameTranslations: acrossLanguages((other) => other?.rooms[room.key].name ?? room.name),
    descriptionTranslations: acrossLanguages((other) => other?.rooms[room.key].description ?? room.description),
  }));
}
